import type { FormField } from '@core-engine/schema/types.js'

export function generateSubmissionDrawer(
  pascal: string,
  kebab: string,
  fields: FormField[],
  label: string,
  includeDynamic: boolean,
  _actionImportPath: string
): string {
  const fieldItems = fields
    .filter((f) => f.name)
    .map((f) => {
      const name = f.name!
      const lbl = f.label

      if (f.type === 'email') {
        return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">
                  {submission.${name} ? (
                    <a href={\`mailto:\${submission.${name}}\`} className="text-primary hover:underline">{submission.${name}}</a>
                  ) : '-'}
                </p>
              </div>`
      }
      if (f.type === 'textarea') {
        return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm whitespace-pre-wrap">{submission.${name} || '-'}</p>
              </div>`
      }
      if (f.type === 'checkbox') {
        return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">{submission.${name} ? 'Yes' : 'No'}</p>
              </div>`
      }
      if (f.type === 'date') {
        return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">{submission.${name} ? new Date(submission.${name}).toLocaleDateString() : '-'}</p>
              </div>`
      }
      if (f.type === 'url') {
        return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">
                  {submission.${name} ? (
                    <a href={submission.${name}} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{submission.${name}}</a>
                  ) : '-'}
                </p>
              </div>`
      }
      if (f.type === 'list' || f.type === 'multiselect') {
        return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <div className="text-sm">
                  {Array.isArray(submission.${name}) && submission.${name}.length > 0 ? (
                    <ul className="list-inside list-disc space-y-1">
                      {submission.${name}.map((item: unknown) => {
                        const itemLabel = typeof item === 'string'
                          ? item
                          : item && typeof item === 'object'
                            ? Object.entries(item).map(([key, value]) => \`\${key}: \${String(value)}\`).join(', ')
                            : String(item)
                        return <li key={itemLabel}>{itemLabel}</li>
                      })}
                    </ul>
                  ) : '-'}
                </div>
              </div>`
      }
      return `              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">${lbl}</p>
                <p className="text-sm">{submission.${name} ?? '-'}</p>
              </div>`
    })
    .join('\n')

  const customFieldsSection = includeDynamic
    ? `
              {submission.customFields && Object.keys(submission.customFields).length > 0 && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-semibold text-muted-foreground">Custom Fields</p>
                  {Object.entries(submission.customFields).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <p className="text-sm font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                      <p className="text-sm">{value !== null && value !== undefined ? String(value) : '-'}</p>
                    </div>
                  ))}
                </div>
              )}`
    : ''

  return `'use client'

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@admin/components/ui/drawer'
import { ScrollArea } from '@admin/components/ui/scroll-area'
import { use${pascal}Submission } from '@admin/hooks/use-${kebab}-form'

interface ${pascal}SubmissionDrawerProps {
  open: boolean
  submissionId: string | null
  onOpenChange: (open: boolean) => void
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'medium'
  }).format(date)
}

export function ${pascal}SubmissionDrawer({
  open,
  submissionId,
  onOpenChange
}: ${pascal}SubmissionDrawerProps) {
  const { data: submission, error, isPending } = use${pascal}Submission(
    open ? submissionId : null
  )
  const description = submission
    ? \`Submitted \${formatDateTime(submission.submittedAt)}\`
    : submissionId
      ? \`Submission \${submissionId}\`
      : 'No submission selected'

  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange} modal>
      <DrawerContent className="h-full w-full overflow-hidden data-[vaul-drawer-direction=right]:sm:max-w-xl">
        <DrawerHeader>
          <DrawerTitle>${label} Submission</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        <ScrollArea className="min-h-0 flex-1">
          {isPending ? (
            <div className="p-4 text-sm text-muted-foreground">Loading submission…</div>
          ) : error ? (
            <div className="p-4 text-sm text-destructive">
              Error loading submission: {error.message}
            </div>
          ) : submission ? (
            <div className="p-4">
              <div className="space-y-4 rounded-sm border border-border bg-card p-4">
${fieldItems}${customFieldsSection}
                <div className="space-y-1 border-t pt-4">
                  <p className="text-sm font-medium text-muted-foreground">Submitted At</p>
                  <p className="text-sm">{formatDateTime(submission.submittedAt)}</p>
                </div>
              </div>
            </div>
          ) : submissionId ? (
            <div className="p-4 text-sm text-muted-foreground">Submission not found.</div>
          ) : null}
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  )
}
`
}
